import type { CanvasElement, TextElement, ButtonElement } from '../types';
import type { CustomFont } from './fontLoader';
import type { FontAlias } from '../stores/fontAliasStore';
import { resolveFontCandidate, type MatchTier } from './fontCandidateResolver';

export type FontResolutionStatus = 'resolved' | 'aliased' | 'heuristic' | 'missing';

export interface FontResolution {
  status: FontResolutionStatus;
  resolvedFamily: string | null;
  resolvedWeight: number | null;
  resolvedStyle: string | null;
  aliasId?: string;
  heuristicTier?: 'normalized' | 'contains' | 'token' | 'filename';
}

const TIER_TO_STATUS: Record<MatchTier, FontResolutionStatus> = {
  exact: 'resolved',
  alias: 'aliased',
  normalized: 'heuristic',
  contains: 'heuristic',
  token: 'heuristic',
  filename: 'heuristic',
  manual: 'aliased',
};

export function resolveFontForCss(
  cssValue: string,
  fonts: CustomFont[],
  aliases: FontAlias[]
): FontResolution {
  const trimmed = (cssValue || '').trim();
  if (!trimmed) {
    return { status: 'missing', resolvedFamily: null, resolvedWeight: null, resolvedStyle: null };
  }

  const candidate = resolveFontCandidate(trimmed, { fonts, aliases });
  if (!candidate) {
    return { status: 'missing', resolvedFamily: null, resolvedWeight: null, resolvedStyle: null };
  }

  const status = TIER_TO_STATUS[candidate.tier];
  const heuristicTier =
    status === 'heuristic'
      ? (candidate.tier as 'normalized' | 'contains' | 'token' | 'filename')
      : undefined;

  return {
    status,
    resolvedFamily: candidate.matchedFamily,
    resolvedWeight: candidate.matchedWeight,
    resolvedStyle: candidate.matchedStyle,
    aliasId: candidate.aliasId,
    heuristicTier,
  };
}

export interface ProjectFontUsageRow {
  cssValue: string;
  elementIds: string[];
  svgFontSource: string | null;
}

export function collectProjectFontUsage(elements: CanvasElement[]): ProjectFontUsageRow[] {
  const byCss = new Map<string, ProjectFontUsageRow>();

  for (const el of elements) {
    if (el.type !== 'text' && el.type !== 'button') continue;
    const te = el as TextElement | ButtonElement;
    const css = (te.fontFamily || '').trim();
    if (!css) continue;

    const row = byCss.get(css) ?? { cssValue: css, elementIds: [], svgFontSource: null };
    row.elementIds.push(te.id);
    if (!row.svgFontSource && te.svgFontSource) row.svgFontSource = te.svgFontSource;
    byCss.set(css, row);
  }

  return Array.from(byCss.values()).sort((a, b) => a.cssValue.localeCompare(b.cssValue));
}
